import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { authAPI, setAuthFailureHandler } from "./api";

const AuthContext = createContext(null);

function clearStoredAuth() {
  localStorage.removeItem("ahc_auth_token");
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const handleAuthFailure = () => {
      clearStoredAuth();
      if (mounted) {
        setUser(null);
      }
    };

    setAuthFailureHandler(handleAuthFailure);

    const token = localStorage.getItem("ahc_auth_token");

    if (!token) {
      setLoading(false);
      return () => {
        mounted = false;
        setAuthFailureHandler(null);
      };
    }

    authAPI
      .me()
      .then(({ data }) => {
        if (mounted) {
          setUser(data.data);
        }
      })
      .catch(() => {
        clearStoredAuth();
        if (mounted) {
          setUser(null);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
      setAuthFailureHandler(null);
    };
  }, []);

  useEffect(() => {
    if (!user) return undefined;

    const renewActiveSession = () => {
      if (document.visibilityState === "visible") {
        authAPI.me().catch(() => {
          // The shared response handler clears the session if it has expired.
        });
      }
    };
    const intervalId = window.setInterval(renewActiveSession, 60 * 60 * 1000);
    document.addEventListener("visibilitychange", renewActiveSession);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", renewActiveSession);
    };
  }, [user]);

  const login = async (identifier, password) => {
    const { data } = await authAPI.login({ identifier, password });
    localStorage.setItem("ahc_auth_token", data.data.token);
    sessionStorage.setItem("ahc_just_logged_in", "true");
    setUser(data.data.user);
    return data.data.user;
  };

  const logout = async () => {
    clearStoredAuth();
    sessionStorage.removeItem("ahc_just_logged_in");
    setUser(null);

    try {
      await authAPI.logout();
    } catch {
      // Token may already be invalid or expired.
    }
  };

  const updateUser = (userData, token) => {
    if (token) {
      localStorage.setItem("ahc_auth_token", token);
    }
    setUser((prev) => ({ ...prev, ...userData }));
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: Boolean(user),
      isAdmin: user?.role === "admin" || user?.role === "superadmin",
      isSuperAdmin: user?.role === "superadmin",
      login,
      logout,
      updateUser,
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
